/**
 * Painel de filtros da biblioteca: busca, empresa, ordenacao, cinco linhas de
 * chips combinaveis (publico, status, categoria, setor, uso) e a troca entre
 * cartoes e tabela.
 *
 * As contagens dos chips vem prontas de quem chama (facetadas em
 * `filtragem.ts`): este componente nao sabe o que e um modelo, so desenha.
 * Isso mantem a regra "quantos sobrariam se eu clicasse aqui" num lugar so.
 *
 * Decisoes nao obvias:
 *  - O seletor de empresa e um `Select`, e nao chips, porque a lista cresce
 *    com o cadastro (centenas de empresas) e tem duas opcoes reservadas
 *    (todas / so globais) que precisam ficar sempre no topo.
 *  - A busca tem atalho de teclado ("/"), anunciado na dica; o atalho mora na
 *    pagina, que sabe se ha modal aberto.
 *  - "Limpar filtros" so aparece com filtro fino ativo e diz quantos sao.
 *  - A troca cartoes/tabela some abaixo de `md`: no celular a lista e sempre
 *    de cartoes.
 */

import { Button, Field, FilterChips, Icon, Input, Panel, Select, type ChipOption } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { ESCOPO_GLOBAIS, ESCOPO_TODAS, type FiltroUso, type FiltrosLocais, type Ordem } from '../filtragem';
import type { TemplateAudience, TemplateCategory, TemplateSector, TemplateStatus } from '../types';
import {
  CATEGORIAS,
  CATEGORIA_LABEL,
  COLUNAS_ORDEM,
  ORDEM_LABEL,
  PUBLICOS,
  PUBLICO_LABEL,
  SETORES,
  SETOR_LABEL,
  STATUS_LABEL_CURTO,
  STATUS_LISTA,
  type ColunaOrdem,
  type Visualizacao,
} from '../vocabulario';

export interface ContagemFacetada {
  readonly total: number;
  readonly por: (valor: string) => number;
}

export interface ContagensDosFiltros {
  readonly categoria: ContagemFacetada;
  readonly setor: ContagemFacetada;
  readonly status: ContagemFacetada;
  readonly publico: ContagemFacetada;
  readonly uso: Readonly<Record<FiltroUso, number>>;
}

function opcoes<T extends string>(todos: T, rotuloTodos: string, valores: readonly T[], rotulo: (v: T) => string, c: ContagemFacetada): readonly ChipOption<T>[] {
  return [{ value: todos, label: rotuloTodos, count: formatCount(c.total) }, ...valores.map((v) => ({ value: v, label: rotulo(v), count: formatCount(c.por(v)) }))];
}

function parseColuna(valor: string): ColunaOrdem | null {
  return COLUNAS_ORDEM.find((c) => c === valor) ?? null;
}

export function FiltrosDeModelos({
  filtros,
  onChange,
  contagens,
  companies,
  ordem,
  onOrdem,
  visualizacao,
  onVisualizacao,
  filtrosAtivos,
  onLimpar,
  idBusca,
}: {
  readonly filtros: FiltrosLocais;
  readonly onChange: (parcial: Partial<FiltrosLocais>) => void;
  readonly contagens: ContagensDosFiltros;
  readonly companies: readonly CompanySummary[];
  readonly ordem: Ordem;
  readonly onOrdem: (ordem: Ordem) => void;
  readonly visualizacao: Visualizacao;
  readonly onVisualizacao: (v: Visualizacao) => void;
  /** Quantos filtros finos estao ligados (busca, publico, status, categoria, setor, uso). */
  readonly filtrosAtivos: number;
  readonly onLimpar: () => void;
  readonly idBusca: string;
}): JSX.Element {
  const publicos = opcoes<TemplateAudience | 'todos'>('todos', 'Todos', PUBLICOS, (v) => (v === 'todos' ? 'Todos' : PUBLICO_LABEL[v]), contagens.publico);
  const status = opcoes<TemplateStatus | 'todos'>('todos', 'Todos', STATUS_LISTA, (v) => (v === 'todos' ? 'Todos' : STATUS_LABEL_CURTO[v]), contagens.status);
  const categorias = opcoes<TemplateCategory | 'todas'>('todas', 'Todas', CATEGORIAS, (v) => (v === 'todas' ? 'Todas' : CATEGORIA_LABEL[v]), contagens.categoria);
  const setores = opcoes<TemplateSector | 'todos'>('todos', 'Todos', SETORES, (v) => (v === 'todos' ? 'Todos' : SETOR_LABEL[v]), contagens.setor);
  const usos: readonly ChipOption<FiltroUso>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(contagens.uso.todos) },
    { value: 'em_uso', label: 'Ligados a régua ou disparo', count: formatCount(contagens.uso.em_uso) },
    { value: 'sem_uso', label: 'Sem uso', count: formatCount(contagens.uso.sem_uso) },
  ];
  const visoes: readonly ChipOption<Visualizacao>[] = [
    { value: 'cartoes', label: 'Cartões' },
    { value: 'tabela', label: 'Tabela' },
  ];

  return (
    <Panel className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_minmax(0,14rem)]">
        <Field label="Buscar" hint="Nome, texto, assunto, botões, público e onde está ligado. Atalho: tecla /" htmlFor={idBusca}>
          {({ id, describedBy }) => (
            <div className="relative">
              <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <Input
                id={id}
                type="search"
                aria-describedby={describedBy}
                value={filtros.busca}
                onChange={(e) => onChange({ busca: e.target.value })}
                placeholder="carrinho_t1h, PIX, vendedora…"
                autoComplete="off"
                className="pl-9"
              />
            </div>
          )}
        </Field>
        <Field label="Empresa" hint="Uma empresa mostra os modelos dela mais os globais.">
          {({ id, describedBy }) => (
            <Select id={id} aria-describedby={describedBy} value={filtros.escopo} onChange={(e) => onChange({ escopo: e.target.value })}>
              <option value={ESCOPO_TODAS}>Todas as empresas</option>
              <option value={ESCOPO_GLOBAIS}>Só modelos globais</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Ordenar por" hint={ordem.desc ? 'Do maior para o menor.' : 'Do menor para o maior.'}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={ordem.coluna}
              onChange={(e) => {
                const coluna = parseColuna(e.target.value);
                if (coluna !== null) {
                  onOrdem({ coluna, desc: coluna !== 'nome' });
                }
              }}
            >
              {COLUNAS_ORDEM.map((c) => (
                <option key={c} value={c}>
                  {ORDEM_LABEL[c]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <div className="flex flex-col gap-3">
        <FilterChips label="Filtrar por público" prefix="Fala com quem:" options={publicos} value={filtros.publico} onChange={(publico) => onChange({ publico })} />
        <FilterChips label="Filtrar por status" prefix="Status:" options={status} value={filtros.status} onChange={(s) => onChange({ status: s })} />
        <div className="flex flex-col gap-3 xl:flex-row xl:gap-8">
          <FilterChips label="Filtrar por categoria" prefix="Categoria:" options={categorias} value={filtros.categoria} onChange={(categoria) => onChange({ categoria })} tone="accent" />
          <FilterChips label="Filtrar por setor" prefix="Setor:" options={setores} value={filtros.setor} onChange={(setor) => onChange({ setor })} tone="accent" />
        </div>
        <FilterChips label="Filtrar por uso" prefix="Uso:" options={usos} value={filtros.uso} onChange={(uso) => onChange({ uso })} tone="accent" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
        <div className="flex items-center gap-2 text-xs text-ink-subtle">
          {filtrosAtivos === 0 ? (
            <span>Nenhum filtro fino ligado.</span>
          ) : (
            <>
              <span>
                {filtrosAtivos} {filtrosAtivos === 1 ? 'filtro ligado' : 'filtros ligados'}
              </span>
              <Button variant="ghost" size="sm" icon="x" onClick={onLimpar}>
                Limpar filtros
              </Button>
            </>
          )}
        </div>
        <div className="hidden md:block">
          <FilterChips label="Visualização" prefix="Ver como:" options={visoes} value={visualizacao} onChange={onVisualizacao} />
        </div>
      </div>
    </Panel>
  );
}
